import React, { useState } from 'react';
import { useEntityList, createEntity, updateEntity, deleteEntity } from '../data/entities';
import { useAuth } from '../AuthContext';

// Compute workloads: run a Node.js script on an ephemeral Fargate task.
// Create → backend compute worker launches → live logTail streams into the
// doc → done/error/stopped. Stop = flip stopRequested (the only lifecycle
// field clients may touch — rules enforce it).
const ACTIVE = ['requested', 'launching', 'running'];

const STATUS_DOT = {
  requested: '◌', launching: '◍', running: '●', done: '✓', error: '✗', stopped: '■',
};

export default function WorkspaceWorkloads({ orgId, workspaceId, compact = false }) {
  const { user } = useAuth();
  const scope = { org: orgId, workspace: workspaceId };
  const { docs: workloads } = useEntityList('workload', scope);
  const [name, setName] = useState('');
  const [script, setScript] = useState("console.log('hello from Fargate');\n");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  return (
    <section style={{ marginTop: compact ? 12 : 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {!compact && <h2 style={{ margin: 0 }}>Compute</h2>}
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'New workload'}
        </button>
      </div>
      {!compact && (
        <p className="cs-help" style={{ marginTop: 4 }}>
          Node.js scripts on ephemeral Fargate tasks — isolated container per run, live logs, 30&nbsp;min cap.
        </p>
      )}

      {showForm && (
        <form
          className="cs-card"
          style={{ padding: 14, marginTop: 12, display: 'grid', gap: 8 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim() || !script.trim() || busy) return;
            setBusy(true);
            setError('');
            try {
              await createEntity('workload', scope, {
                name: name.trim(),
                spec: { script },
                requestedBy: user.uid,
              });
              setName('');
              setShowForm(false);
            } catch (err) {
              setError(err.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <input
            className="cs-input"
            placeholder="Workload name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <textarea
            className="cs-input"
            style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 13 }}
            value={script}
            onChange={(e) => setScript(e.target.value)}
            spellCheck={false}
          />
          <div>
            <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
              Run on Fargate
            </button>
          </div>
        </form>
      )}
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}

      {workloads && !workloads.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>No workloads yet.</div>
      )}
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {(workloads || []).map((w) => (
          <WorkloadCard key={w.id} scope={scope} w={w} />
        ))}
      </div>
    </section>
  );
}

function WorkloadCard({ scope, w }) {
  const [showLogs, setShowLogs] = useState(false);
  const active = ACTIVE.includes(w.status);

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span title={w.status}>{STATUS_DOT[w.status] || '·'}</span>
        <strong style={{ color: 'var(--text)' }}>{w.name}</strong>
        <span className="cs-help" style={{ fontSize: 12 }}>
          {w.status}
          {w.exitDetail && !active ? ` — ${w.exitDetail}` : ''}
        </span>
        <span style={{ flex: 1 }} />
        {(w.logTail || []).length > 0 && (
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowLogs((s) => !s)}>
            {showLogs ? 'Hide logs' : 'Logs'}
          </button>
        )}
        {active && (
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            disabled={w.stopRequested}
            onClick={() => updateEntity('workload', scope, w.id, { stopRequested: true })}
          >
            {w.stopRequested ? 'Stopping…' : 'Stop'}
          </button>
        )}
        {!active && (
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={async () => {
              // eslint-disable-next-line no-alert
              if (window.confirm(`Delete workload "${w.name}"?`)) {
                await deleteEntity('workload', scope, w.id);
              }
            }}
          >
            Delete
          </button>
        )}
      </div>
      {showLogs && (
        <pre
          style={{
            marginTop: 10,
            padding: 10,
            background: '#0b0f19',
            color: '#9ece9e',
            borderRadius: 8,
            fontSize: 12,
            maxHeight: 240,
            overflow: 'auto',
          }}
        >
          {(w.logTail || []).join('\n') || '(no output)'}
        </pre>
      )}
    </div>
  );
}
